import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Fingerprint, Smartphone, Shield, Trash2, Plus, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const BiometricSettings = ({ user, onUpdateUser }) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);

  useEffect(() => {
    checkBiometricSupport();
    fetchCredentials();
  }, []);

  const checkBiometricSupport = () => {
    // Check if WebAuthn is supported
    const supported = window.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function';
    setBiometricSupported(supported);
  };

  const fetchCredentials = async () => {
    try {
      const res = await axios.get(`${API}/auth/biometric/credentials/${user.id}`);
      setCredentials(res.data);
    } catch (error) {
      console.error('Failed to fetch credentials');
    } finally {
      setLoading(false);
    }
  };

  const registerBiometric = async () => {
    if (!biometricSupported) {
      toast.error('Biometric authentication is not supported on this device');
      return;
    }

    setRegistering(true);
    try {
      // Create credential options
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: "SharePay",
          id: window.location.hostname
        },
        user: {
          id: Uint8Array.from(user.id, c => c.charCodeAt(0)),
          name: user.email || user.name || 'SharePay User',
          displayName: user.name || 'SharePay User'
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },  // ES256
          { alg: -257, type: "public-key" } // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required"
        },
        timeout: 60000,
        attestation: "none"
      };

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (credential) {
        // Convert credential to storable format
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        const publicKey = btoa(String.fromCharCode(...new Uint8Array(credential.response.getPublicKey ? credential.response.getPublicKey() : new Uint8Array())));
        
        // Get device name
        const deviceName = navigator.userAgent.includes('iPhone') ? 'iPhone' :
          navigator.userAgent.includes('Android') ? 'Android Device' :
          navigator.userAgent.includes('Mac') ? 'Mac' :
          navigator.userAgent.includes('Windows') ? 'Windows PC' : 'Unknown Device';

        // Register with backend
        await axios.post(
          `${API}/auth/biometric/register?user_id=${user.id}&credential_id=${encodeURIComponent(credentialId)}&public_key=${encodeURIComponent(publicKey)}&device_name=${encodeURIComponent(deviceName)}`
        );

        // Store credential_id locally for biometric login
        localStorage.setItem('biometric_credential_id', credentialId);

        toast.success('Biometric registered successfully! 🔐');
        fetchCredentials();
        
        // Update user state
        if (onUpdateUser) {
          const updatedUser = { ...user, biometric_enabled: true };
          onUpdateUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error('Biometric registration error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Biometric registration was cancelled');
      } else if (error.name === 'NotSupportedError') {
        toast.error('This device does not support biometric authentication');
      } else {
        toast.error('Failed to register biometric. Please try again.');
      }
    } finally {
      setRegistering(false);
    }
  };

  const removeCredential = async (credentialId) => {
    if (!confirm('Remove this biometric credential?')) return;
    
    try {
      // Find the credential to get its credential_id
      const credToRemove = credentials.find(c => c.id === credentialId);
      
      await axios.delete(`${API}/auth/biometric/${credentialId}?user_id=${user.id}`);
      toast.success('Biometric credential removed');
      fetchCredentials();
      
      // Clear localStorage if this was the stored credential
      const storedCredentialId = localStorage.getItem('biometric_credential_id');
      if (credToRemove && storedCredentialId === credToRemove.credential_id) {
        localStorage.removeItem('biometric_credential_id');
      }
      
      // Update user state if no credentials left
      if (credentials.length <= 1) {
        localStorage.removeItem('biometric_credential_id');
        if (onUpdateUser) {
          const updatedUser = { ...user, biometric_enabled: false };
          onUpdateUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      toast.error('Failed to remove credential');
    }
  };

  const testBiometric = async () => {
    if (credentials.length === 0) {
      toast.error('No biometric credentials registered');
      return;
    }

    try {
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyCredentialRequestOptions = {
        challenge: challenge,
        allowCredentials: credentials.map(cred => ({
          id: Uint8Array.from(atob(cred.credential_id), c => c.charCodeAt(0)),
          type: 'public-key',
          transports: ['internal']
        })),
        userVerification: "required",
        timeout: 60000
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      if (assertion) {
        toast.success('Biometric verification successful! ✅');
      }
    } catch (error) {
      console.error('Biometric test error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Biometric verification cancelled');
      } else {
        toast.error('Biometric verification failed');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/profile')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center">
              <Fingerprint className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" data-testid="biometric-title">Biometric Security</h1>
              <p className="text-muted-foreground text-sm">Fingerprint & Face ID authentication</p>
            </div>
          </div>
        </motion.div>

        {/* Support Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`mt-8 rounded-2xl p-5 border ${
            biometricSupported 
              ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' 
              : 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
          }`}
        >
          <div className="flex items-center gap-3">
            {biometricSupported ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-700 dark:text-green-300">Biometric Supported</p>
                  <p className="text-sm text-green-600 dark:text-green-400">Your device supports fingerprint or face authentication</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-700 dark:text-red-300">Not Supported</p>
                  <p className="text-sm text-red-600 dark:text-red-400">Your browser or device doesn't support biometric authentication</p>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Registered Devices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold">Registered Devices</h2>
            </div>
            <span className="text-sm text-muted-foreground">{credentials.length} device(s)</span>
          </div>

          {credentials.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">No biometric credentials registered</p>
              <Button
                onClick={registerBiometric}
                disabled={!biometricSupported || registering}
                className="rounded-full gradient-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                {registering ? 'Registering...' : 'Register This Device'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {credentials.map((cred, index) => (
                <motion.div
                  key={cred.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                      <Fingerprint className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-medium">{cred.device_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Added {new Date(cred.created_at).toLocaleDateString()}
                        {cred.last_used && ` • Last used ${new Date(cred.last_used).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCredential(cred.id)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
              
              <Button
                onClick={registerBiometric}
                disabled={!biometricSupported || registering}
                variant="outline"
                className="w-full rounded-xl mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                {registering ? 'Registering...' : 'Add Another Device'}
              </Button>
            </div>
          )}
        </motion.div>

        {/* Test Biometric */}
        {credentials.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 bg-card rounded-2xl p-6 border border-border"
          >
            <h3 className="font-semibold mb-3">Test Your Biometric</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Verify that your biometric authentication is working correctly
            </p>
            <Button onClick={testBiometric} variant="outline" className="w-full rounded-xl">
              <Fingerprint className="w-4 h-4 mr-2" /> Test Biometric Login
            </Button>
          </motion.div>
        )}

        {/* Security Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6 bg-muted/50 rounded-2xl p-6"
        >
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-violet-600" />
            How it works
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-violet-600 mt-1">•</span>
              Your biometric data (fingerprint/face) never leaves your device
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-600 mt-1">•</span>
              Only a secure cryptographic key is stored on our servers
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-600 mt-1">•</span>
              Use biometrics for quick login and to authorize payments
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-600 mt-1">•</span>
              You can remove registered devices anytime from this page
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default BiometricSettings;
